import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button as ShadcnButton } from './ui/button';

interface SignUpModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: string;
    description?: string;
    primaryButtonText?: string;
    onPrimaryAction?: () => void;
}

const SignUpModal = ({
    open,
    onOpenChange,
    title,
    description,
    primaryButtonText,
    onPrimaryAction,
}: SignUpModalProps) => {
    const { t } = useTranslation();

    const defaultTitle = title || t('signupModal.turnChoresIntoAdventures', 'Turn chores into adventures');
    const defaultDescription = description || t('signupModal.watchKidsBecomeHelpers', 'Watch your kids become your best helpers with interactive checklists and AI-powered characters.');
    const defaultFreeTrialText = t('signupModal.startFreeTrial', 'Start your free trial');
    const defaultPrimaryButtonText = primaryButtonText || t('signupModal.signInWithGoogle', 'Sign in with Google');

    const handlePrimaryAction = () => {
        if (onPrimaryAction) {
            onPrimaryAction();
        } else {
            handleGoogleLogin();
        }
    };

    const handleGoogleLogin = () => {
        const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        if (!googleClientId) {
            console.error("Google Client ID is not defined. Set VITE_GOOGLE_CLIENT_ID in your .env file.");
            alert("Google Client ID is not defined. Please set VITE_GOOGLE_CLIENT_ID in your .env file.");
            return;
        }

        // Check for referral context before redirecting
        const referralCode = sessionStorage.getItem('referralCode');

        console.log('🔗 SignUpModal: Initiating Google OAuth with referral context', {
            service: 'montessori-shopping-list-frontend',
            environment: process.env.NODE_ENV,
            runtime: 'browser',
            hasReferralCode: !!referralCode,
            referralCode: referralCode || null,
            timestamp: new Date().toISOString()
        });

        // This is the URI Google will redirect to after authentication.
        // It must be registered in your Google Cloud Console's "Authorized redirect URIs".
        // This MUST be your BACKEND's callback URL.
        const backendCallbackUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/google/callback`;

        const scope = 'email profile openid'; // Adjust scopes as needed
        const responseType = 'code';

        // State to be sent to Google and returned to your backend.
        // Your backend will use frontendRedirect to send the user back to the correct frontend page with the token.
        // LoginPage is good for this as it already handles tokens in the URL.
        const state = JSON.stringify({ platform: 'web', frontendRedirect: `${window.location.origin}/login` });

        // Construct the Google OAuth URL
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(backendCallbackUrl)}&response_type=${responseType}&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent&state=${encodeURIComponent(state)}`;

        // Redirect the user's browser to Google's authentication page
        window.location.href = googleAuthUrl;
        onOpenChange(false);
    };


    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md border-none bg-linear-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6 sm:p-8 rounded-3xl shadow-2xl" data-testid="signup-modal">
                <DialogTitle className="sr-only" data-testid="modal-title-sr">
                    {defaultTitle}
                </DialogTitle>
                <DialogDescription className="sr-only" data-testid="modal-description-sr">
                    {defaultDescription}
                </DialogDescription>

                {/* Main content */}
                <div className="text-center space-y-6" data-testid="modal-content">
                    {/* Minifil Logo */}
                    <div className="flex justify-center" data-testid="logo-illustration">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white dark:bg-gray-100 rounded-2xl flex items-center justify-center shadow-lg dark:shadow-gray-800/50 overflow-hidden">
                            <img
                                src="/minifil-150.png"
                                alt="Minifil Logo"
                                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                                data-testid="minifil-logo"
                            />
                        </div>
                    </div>

                    {/* Title */}
                    <h2
                        className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 leading-tight"
                        data-testid="modal-title"
                    >
                        {defaultTitle}
                    </h2>

                    {/* Description */}
                    <p
                        className="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed"
                        data-testid="modal-description"
                    >
                        {defaultDescription}
                        <br />
                        <span className="font-semibold text-gray-800 dark:text-white">{defaultFreeTrialText}</span>
                    </p>

                    {/* Google Sign-in Button - Following Google Branding Guidelines */}
                    <ShadcnButton
                        onClick={handlePrimaryAction}
                        className="w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 shadow-sm hover:shadow-md transition-all duration-200 py-3 sm:py-4 text-base sm:text-lg font-medium rounded-xl flex items-center justify-center gap-3"
                        style={{ fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                        variant="outline"
                        data-testid="primary-button"
                    >
                        {/* Google "G" logo with official brand colors */}
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                        </svg>
                        {defaultPrimaryButtonText}
                    </ShadcnButton>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SignUpModal;
